import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Slider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { cloudinaryUrl } from '@realadvisor/cloudinary';
import { useFileDialog } from '@realadvisor/hooks';
import {
  ChevronDown,
  CloudUpload,
  Image as ImageIcon,
  Stamp,
  Trash2,
} from 'lucide-react';
import { useForm } from 'react-hook-form';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useFileUpload } from '@modules/_shared/utils/files/file-upload';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

import { RaSelect } from '../form/RaSelect';
import { SlideDialog } from '../SlideDialog';

import { useCompositeWatermarkImages } from './useCompositeWatermarkImages';
import {
  GET_LOT_WATERMARK_CONFIG,
  UPDATE_LOT_WATERMARK,
} from './watermarkQueries';

type WatermarkPosition =
  | 'top-left'
  | 'top-right'
  | 'center'
  | 'bottom-left'
  | 'bottom-right';

type WatermarkConfig = {
  enabled: boolean;
  position: WatermarkPosition;
  opacity: number;
  scale: number;
  imageUrl: string | null;
  rotation: number;
  positionX: number;
  positionY: number;
};

const POSITION_PRESETS: Record<
  WatermarkPosition,
  { positionX: number; positionY: number }
> = {
  'top-left': { positionX: 4, positionY: 4 },
  'top-right': { positionX: -4, positionY: 4 },
  center: { positionX: 0, positionY: 0 },
  'bottom-left': { positionX: 4, positionY: -4 },
  'bottom-right': { positionX: -4, positionY: -4 },
};

const DEFAULT_CONFIG: WatermarkConfig = {
  enabled: true,
  position: 'bottom-right',
  opacity: 0.6,
  scale: 0.15,
  imageUrl: null,
  rotation: 0,
  positionX: -4,
  positionY: -4,
};

type LabeledSliderProps = {
  label: string;
  displayValue: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
};

const LabeledSlider = ({
  label,
  displayValue,
  value,
  onChange,
  min,
  max,
  step,
}: LabeledSliderProps) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {displayValue}
      </Typography>
    </Box>
    <Slider
      value={value}
      onChange={(_, v) => onChange(v as number)}
      min={min}
      max={max}
      step={step}
    />
  </Box>
);

type WatermarkSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
  lotId: string;
  previewImageUrl?: string | null;
};

export const WatermarkSettingsDialog = ({
  open,
  onClose,
  lotId,
  previewImageUrl,
}: WatermarkSettingsDialogProps) => {
  const { t } = useLocale();
  const [uploadFile, uploading] = useFileUpload('image');
  const [config, setConfig] = useState<WatermarkConfig>(DEFAULT_CONFIG);
  const [showMore, setShowMore] = useState(false);
  const {
    compositeImages,
    loading: compositing,
    error: compositeError,
    reset: resetComposite,
    snackbar: skippedWarningSnackbar,
  } = useCompositeWatermarkImages();

  const { control, watch, setValue } = useForm({
    defaultValues: { position: config.position },
  });

  const positionValue = watch('position');
  useEffect(() => {
    if (positionValue !== config.position) {
      const preset = POSITION_PRESETS[positionValue];
      setConfig(prev => ({
        ...prev,
        position: positionValue,
        positionX: preset.positionX,
        positionY: preset.positionY,
      }));
    }
  }, [positionValue, config.position]);

  const { data: lotData, loading: lotLoading } = useQuery(
    GET_LOT_WATERMARK_CONFIG,
    {
      variables: { lotId },
      skip: !open,
      fetchPolicy: 'network-only',
    },
  );

  const [
    updateLotWatermark,
    { loading: updating, error: updateError, reset: resetUpdate },
  ] = useMutation(UPDATE_LOT_WATERMARK);
  useEffect(() => {
    if (!open || lotLoading) {
      return;
    }

    const lot = lotData?.lots_by_pk;

    if (lot?.watermark_image_url != null) {
      const pos = lot.watermark_position as WatermarkPosition;
      setConfig({
        enabled: lot.watermark_enabled,
        position: pos,
        opacity: Number(lot.watermark_opacity),
        scale: Number(lot.watermark_scale),
        imageUrl: lot.watermark_image_url,
        rotation: lot.watermark_rotation,
        positionX: lot.watermark_position_x,
        positionY: lot.watermark_position_y,
      });
      setValue('position', pos);
    } else {
      setConfig(DEFAULT_CONFIG);
      setValue('position', DEFAULT_CONFIG.position);
    }
  }, [open, lotLoading, lotData, setValue]);

  const openFileDialog = useFileDialog({
    accept: 'image/png,image/svg+xml',
    multiple: false,
    onChange: files => {
      const file = files[0];
      if (!file) {
        return;
      }

      uploadFile(file, ({ data, error }) => {
        if (data) {
          setConfig(prev => ({
            ...prev,
            imageUrl: data.url,
          }));
        }
        if (error) {
          console.error('Watermark upload failed:', error);
        }
      });
    },
  });

  const handleSave = useCallback(() => {
    updateLotWatermark({
      variables: {
        lotId,
        input: {
          watermark_enabled: config.enabled && config.imageUrl != null,
          watermark_position: config.position,
          watermark_opacity: config.opacity,
          watermark_scale: config.scale,
          watermark_image_url: config.imageUrl,
          watermark_rotation: config.rotation,
          watermark_position_x: config.positionX,
          watermark_position_y: config.positionY,
        },
      },
    })
      .then(() => compositeImages(lotId, { awaitRefetchQueries: true }))
      .then(() => {
        onClose();
      })
      .catch(() => {});
  }, [config, lotId, updateLotWatermark, compositeImages, onClose]);

  const positionStyles: Record<WatermarkPosition, CSSProperties> = {
    'top-left': { top: `${config.positionY}%`, left: `${config.positionX}%` },
    'top-right': {
      top: `${config.positionY}%`,
      right: `${-config.positionX}%`,
    },
    center: {
      top: `calc(50% + ${config.positionY}%)`,
      left: `calc(50% + ${config.positionX}%)`,
    },
    'bottom-left': {
      bottom: `${-config.positionY}%`,
      left: `${config.positionX}%`,
    },
    'bottom-right': {
      bottom: `${-config.positionY}%`,
      right: `${-config.positionX}%`,
    },
  };

  return (
    <>
      <SlideDialog
        open={open}
        onClose={onClose}
        title={t('Watermark settings')}
        maxWidth="md"
        fullWidth
        onSave={handleSave}
        loading={updating || compositing || uploading}
        saveText={t('Save')}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            bgcolor: 'grey.50',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('Watermark Configuration')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('Automatically brand your images with a custom logomark')}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(_, checked) =>
                    setConfig(prev => ({ ...prev, enabled: checked }))
                  }
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {config.enabled ? t('Enabled') : t('Disabled')}
                </Typography>
              }
              labelPlacement="start"
              sx={{ m: 0 }}
            />
          </Paper>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              flexGrow: 1,
              minHeight: 0,
              opacity: config.enabled ? 1 : 0.4,
              pointerEvents: config.enabled ? 'auto' : 'none',
              transition: 'opacity 0.2s',
            }}
          >
            <Stack
              spacing={2}
              sx={{
                flexShrink: 0,
                width: { xs: '100%', md: 340 },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  {t('Watermark Image')}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 120,
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: config.imageUrl ? 'transparent' : 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: config.imageUrl
                        ? 'grey.50'
                        : 'background.default',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.2s',
                    }}
                  >
                    {config.imageUrl ? (
                      <>
                        <img
                          src={config.imageUrl}
                          alt={t('Watermark')}
                          style={{
                            maxWidth: '80%',
                            maxHeight: '80%',
                            objectFit: 'contain',
                          }}
                        />
                        <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                          <Tooltip title={t('Remove image')}>
                            <IconButton
                              sx={{
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                color: 'error.main',
                                '&:hover': { bgcolor: 'grey.100' },
                              }}
                              onClick={() =>
                                setConfig(prev => ({
                                  ...prev,
                                  imageUrl: null,
                                }))
                              }
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </>
                    ) : (
                      <Stack
                        spacing={1}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          alignItems: 'center',
                          color: customPalette.gray400,
                        }}
                      >
                        <Stamp size={32} />
                        <Typography variant="body2" color="text.secondary">
                          {t('No image uploaded')}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                  <Button
                    variant={config.imageUrl ? 'outlined' : 'contained'}
                    fullWidth
                    disabled={uploading}
                    loading={uploading}
                    loadingPosition="start"
                    startIcon={
                      config.imageUrl ? (
                        <ImageIcon size={18} />
                      ) : (
                        <CloudUpload size={18} />
                      )
                    }
                    onClick={openFileDialog}
                    disableElevation
                  >
                    {config.imageUrl
                      ? t('Change image')
                      : t('Upload watermark')}
                  </Button>
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  {t('Settings')}
                </Typography>

                <Stack spacing={2}>
                  <RaSelect
                    name="position"
                    control={control}
                    label={t('Position')}
                    options={[
                      { value: 'top-left', label: t('Top left') },
                      { value: 'top-right', label: t('Top right') },
                      { value: 'center', label: t('Center') },
                      { value: 'bottom-left', label: t('Bottom left') },
                      { value: 'bottom-right', label: t('Bottom right') },
                    ]}
                  />

                  <LabeledSlider
                    label={t('Opacity')}
                    displayValue={`${Math.round(config.opacity * 100)}%`}
                    value={config.opacity}
                    onChange={v => setConfig(prev => ({ ...prev, opacity: v }))}
                    min={0.1}
                    max={1}
                    step={0.05}
                  />

                  <LabeledSlider
                    label={t('Scale')}
                    displayValue={`${Math.round(config.scale * 100)}%`}
                    value={config.scale}
                    onChange={v => setConfig(prev => ({ ...prev, scale: v }))}
                    min={0.05}
                    max={1}
                    step={0.05}
                  />
                </Stack>

                <Box sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="text"
                    color="inherit"
                    onClick={() => setShowMore(!showMore)}
                    endIcon={
                      <ChevronDown
                        style={{
                          transform: showMore ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                        }}
                      />
                    }
                    sx={{
                      justifyContent: 'space-between',
                      color: 'text.secondary',
                    }}
                  >
                    {t('Show more')}
                  </Button>
                  <Collapse in={showMore}>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                      <LabeledSlider
                        label={t('Rotation')}
                        displayValue={`${config.rotation}°`}
                        value={config.rotation}
                        onChange={v =>
                          setConfig(prev => ({ ...prev, rotation: v }))
                        }
                        min={-180}
                        max={180}
                        step={1}
                      />

                      <LabeledSlider
                        label={t('Position X')}
                        displayValue={`${config.positionX}%`}
                        value={config.positionX}
                        onChange={v =>
                          setConfig(prev => ({ ...prev, positionX: v }))
                        }
                        min={-50}
                        max={50}
                        step={1}
                      />

                      <LabeledSlider
                        label={t('Position Y')}
                        displayValue={`${config.positionY}%`}
                        value={config.positionY}
                        onChange={v =>
                          setConfig(prev => ({ ...prev, positionY: v }))
                        }
                        min={-50}
                        max={50}
                        step={1}
                      />
                    </Stack>
                  </Collapse>
                </Box>
              </Paper>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                alignSelf: { md: 'flex-start' },
                height: 'fit-content',
                maxHeight: { md: 'calc(100vh - 140px)' },
                position: { md: 'sticky' },
                top: { md: 24 },
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  zIndex: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('Live Preview')}
                </Typography>
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  bgcolor: '#f8f9fa',
                  backgroundImage:
                    'radial-gradient(#e0e0e0 1px, transparent 0)',
                  backgroundSize: '20px 20px',
                  p: 2,
                  position: 'relative',
                  minHeight: { xs: 300, md: 'auto' },
                  overflow: 'auto',
                }}
              >
                {previewImageUrl ? (
                  <Box
                    sx={{
                      position: 'relative',
                      maxWidth: '100%',
                      boxShadow: 3,
                      lineHeight: 0,
                      borderRadius: 1,
                      overflow: 'hidden',
                      m: 'auto',
                    }}
                  >
                    <img
                      src={cloudinaryUrl(
                        { f: 'jpg', w: 800, c: 'fit' },
                        previewImageUrl,
                      )}
                      alt={t('Watermark preview')}
                      style={{
                        maxWidth: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                    {config.imageUrl ? (
                      <img
                        src={config.imageUrl}
                        alt={t('Watermark')}
                        style={{
                          position: 'absolute',
                          ...positionStyles[config.position],
                          width: `${config.scale * 100}%`,
                          height: 'auto',
                          transform: [
                            config.position === 'center'
                              ? 'translate(-50%, -50%)'
                              : '',
                            config.rotation !== 0
                              ? `rotate(${config.rotation}deg)`
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' '),
                          transformOrigin:
                            config.position === 'center' ? 'center' : undefined,
                          opacity: config.opacity,
                          pointerEvents: 'none',
                        }}
                      />
                    ) : null}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    {t('No preview available')}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </SlideDialog>
      <MutationErrorSnackbar
        error={updateError ?? compositeError ?? null}
        onClose={() => {
          resetUpdate();
          resetComposite();
        }}
      />
      {skippedWarningSnackbar}
    </>
  );
};
